import { type TFunction } from '@sortlist-frontend/translation/server-only';

export type PaginatedSeoMeta = { title?: string; description?: string };

/**
 * Differentiated SEO meta for paginated directory pages. For page <= 1 (or a
 * non-finite page) the base values are returned unchanged. For page > 1 the
 * title/description carry a localised page label ("Seite 2", "Page 2", …) so
 * indexable paginated pages have unique meta and do not cannibalise page 1.
 * Only the page word is localised (via the `longtail` namespace); assembly is
 * done in code. The `defaultValue` keeps it working if a locale lacks the key.
 */
export const buildPaginatedSeoMeta = (
  baseTitle: string | undefined,
  baseDescription: string | undefined,
  page: number,
  t: TFunction,
): PaginatedSeoMeta => {
  if (!Number.isFinite(page) || page <= 1) {
    return { title: baseTitle, description: baseDescription };
  }

  const pageLabel = t('longtail:pagination.pageLabel', { defaultValue: 'Page {{page}}', page });

  return {
    title: baseTitle != null ? `${baseTitle} – ${pageLabel}` : pageLabel,
    description: baseDescription != null ? `${pageLabel}. ${baseDescription}` : pageLabel,
  };
};
